
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Plus, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface FarmGoalsManagerProps {
  goals: string[];
  onGoalsChange: (goals: string[]) => void;
  isEditing: boolean;
}

export const FarmGoalsManager = ({ goals, onGoalsChange, isEditing }: FarmGoalsManagerProps) => {
  const [newGoal, setNewGoal] = useState('');

  const addGoal = () => {
    if (newGoal.trim() && !goals.includes(newGoal.trim())) {
      onGoalsChange([...goals, newGoal.trim()]);
      setNewGoal('');
    }
  };

  const removeGoal = (goalToRemove: string) => {
    onGoalsChange(goals.filter(goal => goal !== goalToRemove));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addGoal();
    }
  };

  if (!isEditing && goals.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Farm Goals</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2">
          {goals.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {goals.map((goal, index) => (
                <Badge
                  key={index}
                  variant="outline"
                  className="bg-green-50 text-green-700 border-green-200 px-2 py-0.5 text-xs"
                >
                  {goal}
                  {isEditing && (
                    <button
                      onClick={() => removeGoal(goal)}
                      className="ml-1 text-green-600 hover:text-green-800"
                    >
                      <X size={10} />
                    </button>
                  )}
                </Badge>
              ))}
            </div>
          )}
          
          {isEditing && (
            <div className="flex gap-2">
              <Input
                placeholder="Add a new farm goal..."
                value={newGoal}
                onChange={(e) => setNewGoal(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1 h-8 text-sm"
              />
              <Button
                onClick={addGoal}
                size="sm"
                disabled={!newGoal.trim()}
                className="bg-green-600 hover:bg-green-700 h-8 px-2"
              >
                <Plus size={14} />
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
